import { useMemo } from 'react';

import { countriesToRegion } from '@/lists/countriesToRegion';
import { isOnPrem } from '@/utils/buildProfile';
import { type CtaSelector } from '@/utils/country-config.types';
import { Region } from '@heidi/schema/ml-scribe';

import {
  getMeetingLinkForCountry,
  getOnboardingCtaForCountry,
  isWebinarEnabledForCountry,
} from './country-config';
import { getHasBookedDemo } from './onboardingDemoState';

/**
 * Region buckets that have dedicated live-onboarding webinars (see
 * `WEBINAR_CONFIGS` in `pages/api/hubspot/webinar-utils.ts`). This is
 * intentionally finer-grained than the `Region` enum — which has no ES/FR/ZA
 * and would collapse them into `EU` (then routed to the UK webinars).
 */
export type WebinarRegion = 'AU' | 'UK' | 'US' | 'FR' | 'ES' | 'ZA';

// Country codes with their own dedicated webinars. These override the coarse
// `Region` bucketing, which would otherwise route e.g. Spain (Region.EU) to
// the UK webinars (GMT/BST) instead of the local ES sessions (CET).
const COUNTRY_CODE_TO_WEBINAR_REGION: Record<string, WebinarRegion> = {
  ES: 'ES',
  FR: 'FR',
  ZA: 'ZA',
};

// Fallback for countries without a dedicated webinar: map the coarse `Region`
// to the nearest webinar bucket (mirrors `REGION_TO_WEBINAR_REGION` in
// `webinar-utils.ts`, e.g. EU -> UK, CA -> US).
const REGION_TO_WEBINAR_REGION: Partial<Record<Region, WebinarRegion>> = {
  [Region.AU]: 'AU',
  [Region.UK]: 'UK',
  [Region.US]: 'US',
  [Region.CA]: 'US',
  [Region.EU]: 'UK',
};

/**
 * Resolves the webinar bucket for a user, preferring a country-level dedicated
 * webinar (ES/FR/ZA) before falling back to the coarse `Region` mapping.
 */
export const getWebinarRegionForCountry = (
  countryCode: string | undefined,
  countryName: string | undefined
): WebinarRegion | undefined => {
  const cc = countryCode?.toUpperCase();
  if (cc && COUNTRY_CODE_TO_WEBINAR_REGION[cc]) {
    return COUNTRY_CODE_TO_WEBINAR_REGION[cc];
  }
  const region = countryName ? countriesToRegion[countryName] : undefined;
  return region ? REGION_TO_WEBINAR_REGION[region] : undefined;
};

export type OnboardingExperience =
  | { type: 'demo'; meetingUrl: string }
  | { type: 'request-demo' }
  | { type: 'webinar'; region: WebinarRegion }
  | { type: 'hidden' };

export interface ResolveOnboardingExperienceArgs {
  countryCode?: string;
  countryName?: string;
  languageCode?: string | null;
  hasBookedDemo: boolean;
}

// Try to render a single CTA. Returns null if the supporting data isn't
// available (e.g. `meeting` with no `meetingLink`), so the caller can fall
// through to the configured fallback or the universal `request-demo` default.
// hasBookedDemo gates only the `meeting` branch — booked users skip duplicate
// calendars, but other CTAs still render.
const tryRenderCta = (
  cta: CtaSelector,
  args: ResolveOnboardingExperienceArgs
): OnboardingExperience | null => {
  const { countryCode, countryName, languageCode, hasBookedDemo } = args;

  // Per-country kill switch: resolves to `hidden` unconditionally, so the
  // caller short-circuits before the universal `request-demo` fallback and the
  // market sees no demo/webinar CTA at all.
  if (cta === 'hidden') {
    return { type: 'hidden' };
  }

  if (cta === 'request-demo') {
    return { type: 'request-demo' };
  }

  if (cta === 'meeting') {
    const meetingUrl = getMeetingLinkForCountry(countryCode, languageCode);
    if (meetingUrl && !hasBookedDemo) {
      return { type: 'demo', meetingUrl };
    }
    return null;
  }

  if (cta === 'webinar') {
    if (isWebinarEnabledForCountry(countryCode) && countryName) {
      const region = getWebinarRegionForCountry(countryCode, countryName);
      if (region) {
        return { type: 'webinar', region };
      }
    }
    return null;
  }

  return null;
};

// `request-demo` is the universal default — every country gets the SDR
// lead-capture form unless `country-config.onboardingCta` explicitly picks
// another primary (with optional fallback).
export const resolveOnboardingExperience = (
  args: ResolveOnboardingExperienceArgs
): OnboardingExperience => {
  // ON-PREM-GATE: airgapped builds have no HubSpot SDR / webinar backend, so
  // every demo and webinar opt-in must be suppressed. `isOnPrem` folds to a
  // literal at build time, so the demo/webinar branches dead-code-eliminate
  // from the on-prem bundle entirely (mirrors the F1 provider gates). This is
  // the single chokepoint for both the render hook (`useOnboardingExperience`)
  // and the submission paths (`useOnboardingCompletion`, `useOrganisationSurvey`).
  if (isOnPrem) {
    return { type: 'hidden' };
  }

  const ctaConfig = getOnboardingCtaForCountry(args.countryCode, args.languageCode);

  if (ctaConfig) {
    const primary = tryRenderCta(ctaConfig.primary, args);
    if (primary) return primary;

    if (ctaConfig.fallback) {
      const fallback = tryRenderCta(ctaConfig.fallback, args);
      if (fallback) return fallback;
    }
  }

  return { type: 'request-demo' };
};

export interface UseOnboardingExperienceArgs {
  countryCode?: string;
  countryName?: string;
  languageCode?: string | null;
}

export const useOnboardingExperience = ({
  countryCode,
  countryName,
  languageCode,
}: UseOnboardingExperienceArgs): OnboardingExperience => {
  // Read once per render — `getHasBookedDemo` is a localStorage probe, so the
  // memo needs it as a dep to recompute after `MeetingBooker` calls
  // `markDemoBooked` mid-session.
  const hasBookedDemo = getHasBookedDemo();
  return useMemo(
    () =>
      resolveOnboardingExperience({
        countryCode,
        countryName,
        languageCode,
        hasBookedDemo,
      }),
    [countryCode, countryName, languageCode, hasBookedDemo]
  );
};
